/**
 * RUMI quiz results — page behaviour.
 *
 * Reads the dog's answers, writes them into the page and filters the bundle to
 * the recommendation. All scoring, slugs, validation and decoding come from
 * window.RumiQuiz (assets/rumi-quiz-logic.js) — the shared contract with the
 * quiz. Nothing here may fork it.
 *
 * Two rules this file exists to keep:
 *   1. Everything written into the DOM goes through textContent. decodeState
 *      already clamps and validates, but the dog's name and breed are still
 *      visitor-supplied strings.
 *   2. Prices are never formatted here. Liquid renders them; JS only shows,
 *      hides and reorders the cards.
 */
(function () {
  'use strict';

  /* Three sections on this template each load this file, so the browser
     executes it three times. Without this guard every listener binds three
     times and one click on "add the bundle" fired three concurrent
     /cart/add.js posts that raced each other. Idempotent by design: sections
     can be reordered or removed in the theme editor. */
  if (window.__rumiQuizResultsLoaded) return;
  window.__rumiQuizResultsLoaded = true;

  var RQ = window.RumiQuiz;
  if (!RQ) return;

  /* ---------------------------------------------------------------
     State
     --------------------------------------------------------------- */

  /**
   * The URL is the primary source so an emailed link reproduces the plan
   * exactly; sessionStorage is only the same-session fallback. Stored state is
   * round-tripped back through the contract's own encode/decode rather than
   * trusted, so a tampered storage entry is clamped the same way a hand-edited
   * link is.
   */
  function readState() {
    var fromUrl = RQ.decodeState(window.location.search);
    if (hasAnswers(fromUrl)) return fromUrl;

    var raw = null;
    try {
      raw = window.sessionStorage.getItem(RQ.STORAGE_KEY);
    } catch (e) {
      /* private mode, or storage disabled */
    }
    if (!raw) return fromUrl;

    var saved;
    try {
      saved = JSON.parse(raw);
    } catch (e) {
      return fromUrl;
    }
    if (!saved || typeof saved !== 'object') return fromUrl;

    var answers = saved.answers && typeof saved.answers === 'object' ? saved.answers : saved;
    return RQ.decodeState(
      RQ.encodeState({
        name: answers.name,
        weightKg: answers.weightKg === undefined ? answers.weight : answers.weightKg,
        dob: answers.dob,
        breed: answers.breed,
        concerns: Array.isArray(answers.concerns) ? answers.concerns : []
      })
    );
  }

  function hasAnswers(state) {
    return !!(state.name || state.weightKg || state.dob || state.breed || state.concerns.length);
  }

  /* ---------------------------------------------------------------
     Formatting helpers
     --------------------------------------------------------------- */

  function fill(template, values) {
    var out = template || '';
    Object.keys(values).forEach(function (key) {
      out = out.split('[' + key + ']').join(values[key]);
    });
    return out;
  }

  function trimNumber(value) {
    return String(Math.round(value * 100) / 100);
  }

  function formatDate(dob, locale) {
    var date = new Date(dob + 'T00:00:00');
    if (isNaN(date.getTime())) return '';
    // The storefront lang is plain "en", which formats US-style. The section
    // pins a BCP 47 tag so the date reads the way the design shows it.
    locale = locale || document.documentElement.lang || undefined;
    try {
      return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return dob;
    }
  }

  function formatAge(age, yearsLabel, monthsLabel) {
    if (!age) return '';
    var parts = [];
    if (age.years) parts.push(age.years + ' ' + yearsLabel);
    if (age.months || !age.years) parts.push(age.months + ' ' + monthsLabel);
    return parts.join(' ');
  }

  /* ---------------------------------------------------------------
     Hero — name, profile card, copy button
     --------------------------------------------------------------- */

  /**
   * textContent, never innerHTML: the name is a visitor-supplied string.
   * Each slot already renders its own section's fallback, so an empty name
   * simply leaves what Liquid wrote — no section needs a shared default.
   */
  function applyNames(state) {
    if (!state.name) return;
    var nodes = document.querySelectorAll('[data-qr-name]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = state.name;
    }
  }

  function applyProfile(root, state) {
    var profile = root.querySelector('[data-qr-profile]');
    if (!profile) return;

    var values = {};

    if (state.weightKg) {
      values.weight = fill(root.getAttribute('data-weight-format'), {
        kg: trimNumber(state.weightKg),
        lb: trimNumber(RQ.toPounds(state.weightKg))
      });
    }

    if (state.dob) {
      var date = formatDate(state.dob, root.getAttribute('data-date-locale'));
      var age = formatAge(
        RQ.ageFrom(state.dob),
        root.getAttribute('data-age-years') || '',
        root.getAttribute('data-age-months') || ''
      );
      if (date) values.birthday = age ? fill(root.getAttribute('data-birthday-format'), { date: date, age: age }) : date;
    }

    if (state.breed) values.breed = state.breed;

    if (state.concerns.length) {
      var separator = root.getAttribute('data-concern-separator') || ', ';
      values.concerns = state.concerns
        .map(function (id) {
          var concern = RQ.concernById(id);
          return concern ? concern.label : '';
        })
        .filter(Boolean)
        .join(separator);
    }

    var shown = 0;
    var facts = profile.querySelectorAll('[data-qr-fact]');
    for (var i = 0; i < facts.length; i++) {
      var key = facts[i].getAttribute('data-qr-fact');
      var value = values[key];
      var slot = facts[i].querySelector('[data-qr-fact-value]');
      if (value && slot) {
        slot.textContent = value;
        facts[i].hidden = false;
        shown++;
      }
    }

    var empty = profile.querySelector('[data-qr-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  /**
   * Async clipboard first, with a selection-based fallback for the browsers and
   * insecure contexts where it is unavailable. Either way the visitor gets a
   * visible, announced confirmation.
   */
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }

    return new Promise(function (resolve, reject) {
      var field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.top = '-1000px';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();

      var copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (e) {
        copied = false;
      }
      document.body.removeChild(field);
      copied ? resolve() : reject(new Error('copy failed'));
    });
  }

  function bindCopy(root) {
    var button = root.querySelector('[data-qr-copy]');
    var status = root.querySelector('[data-qr-copy-status]');
    if (!button) return;

    button.addEventListener('click', function () {
      var code = button.getAttribute('data-qr-copy') || '';
      copyText(code)
        .then(function () {
          if (status) status.textContent = button.getAttribute('data-copied') || '';
        })
        .catch(function () {
          if (status) status.textContent = button.getAttribute('data-failed') || '';
        });
    });
  }

  /* ---------------------------------------------------------------
     Bundle
     --------------------------------------------------------------- */

  function applyBundle(root, state, recommendation, answered) {
    var lead = root.querySelector('[data-qr-lead]');
    var fallbackLead = root.querySelector('[data-qr-fallback-lead]');
    if (fallbackLead) {
      fallbackLead.hidden = answered;
      if (lead) lead.hidden = !answered;
    }

    var grid = root.querySelector('[data-qr-grid]');
    if (!grid) return;

    var handles = recommendation.handles;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-qr-product]'));

    // The gut-brain note only appears when one of the anxiety-cluster concerns
    // was actually selected. It speaks to gut health, never to calming.
    var gutBrain = (root.getAttribute('data-gut-brain-concerns') || '')
      .split(',')
      .map(function (id) { return id.trim(); })
      .filter(Boolean);
    var showNote = state.concerns.some(function (id) { return gutBrain.indexOf(id) !== -1; });

    var shown = 0;
    handles.forEach(function (handle) {
      cards.forEach(function (card) {
        if (card.getAttribute('data-qr-product') !== handle) return;
        card.hidden = false;
        // Reordering by re-appending puts the highest-scoring product first.
        grid.appendChild(card);
        shown++;
      });
    });

    cards.forEach(function (card) {
      if (handles.indexOf(card.getAttribute('data-qr-product')) === -1) card.hidden = true;
      var note = card.querySelector('[data-qr-why]');
      if (note) note.hidden = !(showNote && !card.hidden);
    });

    grid.setAttribute('data-count', String(shown));
  }

  function bindAddToCart(root) {
    var button = root.querySelector('[data-qr-add]');
    var status = root.querySelector('[data-qr-add-status]');
    var label = root.querySelector('[data-qr-add-label]');
    var grid = root.querySelector('[data-qr-grid]');
    if (!button || !grid) return;

    button.addEventListener('click', function () {
      var items = Array.prototype.slice
        .call(grid.querySelectorAll('[data-qr-product][data-variant-id]'))
        .filter(function (card) { return !card.hidden; })
        .map(function (card) {
          return { id: parseInt(card.getAttribute('data-variant-id'), 10), quantity: 1 };
        })
        .filter(function (item) { return !isNaN(item.id); });

      if (!items.length) {
        if (status) status.textContent = button.getAttribute('data-failed') || '';
        return;
      }

      function settle(message) {
        button.disabled = false;
        if (label) label.textContent = button.getAttribute('data-label') || '';
        if (status) status.textContent = message || '';
      }

      button.disabled = true;
      if (label) label.textContent = button.getAttribute('data-busy') || label.textContent;
      if (status) status.textContent = '';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          return response.json();
        })
        .then(function () {
          /* Settle first, redirect second. The cart page is a full load behind
             several apps, and leaving the button greyed out and reading
             "Adding…" for the whole of it looked like the add had hung — the
             shopper had no way to tell it had worked. */
          settle(button.getAttribute('data-added') || '');
          refreshCartCount();
          if (button.getAttribute('data-go-to-cart') === 'true') {
            /* Long enough for the settled state to paint before the browser
               tears the page down — otherwise the confirmation never reaches
               the screen and the redirect is the only feedback there is. */
            setTimeout(function () {
              window.location.href = button.getAttribute('data-cart-url') || '/cart';
            }, 350);
          }
        })
        .catch(function () {
          settle(button.getAttribute('data-failed') || '');
        });
    });
  }

  /** Mirrors the header bubble update the product page already does. */
  function refreshCartCount() {
    fetch('/cart.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var bubble = document.querySelector('.cart-count-bubble');
        if (!bubble) return;
        var count = bubble.querySelector('span[aria-hidden="true"]');
        if (count) count.textContent = cart.item_count;
        var announced = bubble.querySelector('.visually-hidden');
        if (announced) announced.textContent = cart.item_count + ' items';
        bubble.removeAttribute('hidden');
      })
      .catch(function () { /* the bubble is cosmetic — never block on it */ });
  }

  /* ---------------------------------------------------------------
     Closing offer card
     --------------------------------------------------------------- */

  function applyOffer(root, recommendation) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-qr-product]'));
    if (!cards.length) return;

    var top = recommendation.handles[0];
    var match = cards.filter(function (card) {
      return card.getAttribute('data-qr-product') === top;
    })[0];
    if (!match) match = cards[0];

    cards.forEach(function (card) { card.hidden = card !== match; });
  }

  /* ---------------------------------------------------------------
     Boot
     --------------------------------------------------------------- */

  function init() {
    var hero = document.querySelector('[data-rumi-quiz-results]');
    var bundle = document.querySelector('[data-qr-bundle]');
    var offer = document.querySelector('[data-qr-offer]');
    if (!hero && !bundle && !offer) return;

    var state = readState();
    var answered = hasAnswers(state);
    var recommendation = RQ.recommend(state.concerns);

    applyNames(state);

    if (hero) {
      applyProfile(hero, state);
      bindCopy(hero);
    }

    if (bundle) {
      applyBundle(bundle, state, recommendation, answered);
      bindAddToCart(bundle);
    }

    if (offer) applyOffer(offer, recommendation);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
